// /simplify wave (fix B) — shared team-tree helpers for the teams admin
// feature. TeamsAdminPage.buildRows and TeamDialog's parent-select each
// built their own ad-hoc parentTeamId adjacency map; this is the one home
// for that adjacency build + descendant walk so the two can't drift.
//
// NOTE (T1 review): buildRows silently omits teams whose parent chain is
// cycle-corrupted (unreachable from a root). The service rejects cycles at
// write (assertNoCycle), so this is defense-in-depth display behavior, not
// a data path — if a team ever vanishes from the tree, check for manual DB
// edits that introduced a cycle.
import type { TeamRecord } from "../api/client";

/**
 * Group `teams` by `parentTeamId`. The key is `null` for top-level teams —
 * callers that need a sentinel root key (e.g. TeamsAdminPage's
 * archived-parent-promotes-to-root rule) normalize `parentTeamId` on their
 * input teams before calling this, rather than this function inventing a
 * sentinel itself.
 */
export function buildChildrenIndex(teams: TeamRecord[]): Map<string | null, TeamRecord[]> {
  const index = new Map<string | null, TeamRecord[]>();
  for (const t of teams) {
    const list = index.get(t.parentTeamId) ?? [];
    list.push(t);
    index.set(t.parentTeamId, list);
  }
  return index;
}

/**
 * Depth-first collection of every descendant of `rootId` within the given
 * adjacency `index` (as built by {@link buildChildrenIndex}). Cycle-safe:
 * the `result` membership check stops a malformed/cyclic parentTeamId chain
 * from ever revisiting a node. Team counts are small, so an explicit stack
 * (not recursion) is just a style choice, not a perf necessity.
 */
export function collectDescendantIds(
  rootId: string,
  index: Map<string | null, TeamRecord[]>,
): Set<string> {
  const result = new Set<string>();
  const stack = [...(index.get(rootId) ?? [])];
  while (stack.length) {
    const team = stack.pop()!;
    if (result.has(team.id)) continue;
    result.add(team.id);
    for (const child of index.get(team.id) ?? []) stack.push(child);
  }
  return result;
}

/**
 * Task 4 (team-hierarchy-t4) — client-side AFFORDANCE approximation of the
 * server's `isAncestorOrLead`: does the viewer lead `teamId` or any of its
 * ancestors, per the tree data on hand? Delegation-derived lead rights are
 * NOT computable here (the tree carries no delegation rows) — the server
 * gate on POST/DELETE /admin/teams/:id/delegations is authoritative; a
 * delegate-led viewer just doesn't get this shortcut affordance (the button
 * stays hidden even though the server would allow the call). Cycle-safe via
 * the `seen` set — a corrupted parentTeamId chain terminates instead of
 * looping.
 */
export function leadsTeamOrAncestor(
  viewerUserId: string,
  teamId: string,
  teams: { id: string; parentTeamId: string | null; supervisorUserId: string }[],
): boolean {
  const byId = new Map(teams.map((t) => [t.id, t]));
  let cursor: string | null = teamId;
  const seen = new Set<string>();
  while (cursor && !seen.has(cursor)) {
    seen.add(cursor);
    const t = byId.get(cursor);
    if (!t) return false;
    if (t.supervisorUserId === viewerUserId) return true;
    cursor = t.parentTeamId;
  }
  return false;
}
